import type { CurationResponse } from '@/types/Curation.types';
import type { Product } from '@/types/Product.types';
import slaydApi from './axiosInstance';

export interface CreateCurationPayload {
  title?: string;
  description?: string;
  curatorName?: string;
  curator?: number;
  myntraProductIds?: number[];
  relatedCurationIds?: number[];
  thumbnailProducts?: number[];
  viewCount?: number;
  slug?: string;
  isHomepageCuration?: boolean;
  homepageThumbnails?: string[];
  widgetImage?: string;
  curationImage?: string;
  imageTitle?: string;
}

export interface CuratorDeeplinkPayload {
  curatorName: string;
  instagramName: string;
  postId: string | number;
  curationId: number | string;
  variant: string;
}

export interface CuratorDeeplinkResponse {
  deepLink: string;
  message: string;
}

export interface UpdateCurationPayload {
  title?: string;
  description?: string;
  curatorName?: string;
  myntraProductIds?: number[];
  widgetImage?: string;
  curationImage?: string;
  imageTitle?: string;
  isHomepageCuration?: boolean;
}

export interface CreateCurationResponse {
  id: number;
  title?: string;
  curation_image?: string;
  description?: string;
  products?: Product[];
  related_curations?: unknown[];
  curator_name?: string;
  created_at?: string;
  view_count?: number;
  slug?: string;
  is_homepage_curation?: boolean;
  homepage_thumbnails?: string[];
  widget_image?: string;
  image_title?: string;
}

/**
 * Fetch a single curation by ID
 */
export async function fetchCurationById(
  id: number | string
): Promise<CurationResponse> {
  try {
    const response = await slaydApi.get<CurationResponse>(
      `/api/curations/${id}/`
    );

    if (!response.data) throw new Error('Invalid Data');

    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(`Error fetching curation data`);
  }
}

export async function createCuration(
  payload: CreateCurationPayload
): Promise<CreateCurationResponse> {
  try {
    const requestBody: Record<string, unknown> = {};

    if (payload.title !== undefined) requestBody.title = payload.title;
    if (payload.description !== undefined)
      requestBody.description = payload.description;
    if (payload.curatorName !== undefined)
      requestBody.curator_name = payload.curatorName;
    if (payload.curator !== undefined) requestBody.curator = payload.curator;
    if (payload.myntraProductIds !== undefined)
      requestBody.myntra_product_ids = payload.myntraProductIds;
    if (payload.relatedCurationIds !== undefined)
      requestBody.related_curation_ids = payload.relatedCurationIds;
    if (payload.thumbnailProducts !== undefined)
      requestBody.thumbnail_products = payload.thumbnailProducts;
    if (payload.viewCount !== undefined)
      requestBody.view_count = payload.viewCount;
    if (payload.slug !== undefined) requestBody.slug = payload.slug;
    if (payload.isHomepageCuration !== undefined)
      requestBody.is_homepage_curation = payload.isHomepageCuration;
    if (payload.homepageThumbnails !== undefined)
      requestBody.homepage_thumbnails = payload.homepageThumbnails;
    if (payload.widgetImage !== undefined)
      requestBody.widget_image = payload.widgetImage;
    if (payload.curationImage !== undefined)
      requestBody.curation_image = payload.curationImage;
    if (payload.imageTitle !== undefined)
      requestBody.image_title = payload.imageTitle;

    const response = await slaydApi.post<CreateCurationResponse>(
      '/api/curations/create/',
      requestBody
    );

    if (!response.data) throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Error creating curation');
  }
}

/**
 * Update an existing curation
 */
export async function updateCuration(
  id: number | string,
  payload: UpdateCurationPayload
): Promise<CreateCurationResponse> {
  try {
    const requestBody: Record<string, unknown> = {};

    if (payload.title !== undefined) requestBody.title = payload.title;
    if (payload.description !== undefined)
      requestBody.description = payload.description;
    if (payload.curatorName !== undefined)
      requestBody.curator_name = payload.curatorName;
    if (payload.myntraProductIds !== undefined)
      requestBody.myntra_product_ids = payload.myntraProductIds;
    if (payload.widgetImage !== undefined)
      requestBody.widget_image = payload.widgetImage;
    if (payload.curationImage !== undefined)
      requestBody.curation_image = payload.curationImage;
    if (payload.imageTitle !== undefined)
      requestBody.image_title = payload.imageTitle;
    if (payload.isHomepageCuration !== undefined)
      requestBody.is_homepage_curation = payload.isHomepageCuration;

    const response = await slaydApi.patch<CreateCurationResponse>(
      `/api/curations/${id}/update/`,
      requestBody
    );

    if (!response.data) throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Error updating curation');
  }
}

/**
 * Create creator deeplink mapping via key and curation id
 */
export async function generateCuratorDeeplink(
  payload: CuratorDeeplinkPayload
): Promise<CuratorDeeplinkResponse> {
  try {
    const requestBody = {
      curation_id: payload.curationId,
      creator_name: payload.curatorName,
      ig_account_name: payload.instagramName,
      post_id: payload.postId,
      variant: payload.variant,
    };

    const response = await slaydApi.post<CuratorDeeplinkResponse>(
      '/activity/creator-deeplink/',
      requestBody
    );

    if (!response.data) throw new Error('Invalid response');

    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Error creating creator deeplink');
  }
}
